import type { Option } from "../../core/option.ts";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";

import type { CellEntry, CellValue } from "../../core/dtype.ts";
import type { Graph, PointY } from "../../stores/graph-types.ts";

/**
 * Renamed to `CellEntry.Timepoint` (see `dtype.ts`). Re-exported
 * here so existing imports keep working until the rename ripple
 * is finished.
 */
export type TimepointCellEntry = CellEntry.Timepoint;

/** One series a data row yields: its label, key, type and point count. */
export type SeriesResult = {
  label: string;
  seriesKey: string;
  seriesType: string;
  pointCount: number;
  points: [number, PointY][];
};

/**
 * Extract series data from Graph.Props by running each series
 * adapter against the given cells. Operates on the derived
 * Graph.Props type, not raw preset specs.
 *
 * Three adapter paths:
 *   1. groupAdapter — batch: collects all standard + custom cells, calls once
 *   2. adapter on standard cells — per-cell: adapter(partial, null, data)
 *   3. adapter on custom cells — per-cell: adapter(null, dtr, data)
 */
export function extractSeries(
  props: Graph.Props,
  cells: readonly CellEntry[],
  customCells: Option<readonly TimepointCellEntry[]>,
): SeriesResult[] {
  const results: SeriesResult[] = [];
  for (const s of props.series) {
    const { adapter, groupAdapter } = s;
    if (!adapter && !groupAdapter) continue;

    const allPoints: [number, PointY][] = [];

    try {
      if (groupAdapter) {
        const standardPairs: [NaiveDate.Partial, NonNullable<CellValue>][] = [];
        for (const cell of cells) {
          if (cell.value != null) {
            standardPairs.push([cell.date, cell.value]);
          }
        }
        const customPairs: [DateTime.Range<Utc>, NonNullable<CellValue>][] = [];
        if (customCells) {
          for (const c of customCells) {
            if (c.value != null) {
              customPairs.push([c.dtr, c.value]);
            }
          }
        }
        const pts = groupAdapter(standardPairs, customPairs);
        if (pts) {
          for (const pt of pts) {
            if (typeof pt[0] === "number") allPoints.push(pt);
          }
        }
      } else if (adapter) {
        for (const cell of cells) {
          if (cell.value == null) continue;
          const pts = adapter(cell.date, null, cell.value);
          if (pts) {
            for (const pt of pts) {
              if (typeof pt[0] === "number") allPoints.push(pt);
            }
          }
        }
        if (customCells) {
          for (const c of customCells) {
            if (c.value == null) continue;
            const pts = adapter(null, c.dtr, c.value);
            if (pts) {
              for (const pt of pts) {
                if (typeof pt[0] === "number") allPoints.push(pt);
              }
            }
          }
        }
      }
    } catch (err) {
      Tracing.log("series-index/adapter-threw", {
        key: s.key,
        type: s.type,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    results.push({
      label: s.label ?? s.key,
      seriesKey: s.key,
      seriesType: s.type,
      pointCount: allPoints.length,
      points: allPoints,
    });
  }
  return results;
}
